import React, { useMemo } from 'react';
import { useSearchParams, Link } from 'react-router-dom';

type TermTab = 'uso' | 'compras' | 'privacidade';

export const TermsPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  const activeTab: TermTab = useMemo(() => {
    const tab = searchParams.get('tab') || searchParams.get('doc');
    if (tab === 'compras' || tab === 'pagamentos' || tab === 'ingressos') return 'compras';
    if (tab === 'privacidade' || tab === 'cookies' || tab === 'lgpd') return 'privacidade';
    return 'uso';
  }, [searchParams]);

  const setTab = (tab: TermTab) => {
    setSearchParams({ tab });
  };

  const tabs: { id: TermTab; title: string }[] = [
    { id: 'uso', title: 'Termos de Uso do Cardumy' },
    { id: 'compras', title: 'Termos de Compras, Pagamentos e Ingressos' },
    { id: 'privacidade', title: 'Política de Privacidade e Cookies' }
  ];

  return (
    <div className="max-w-3xl mx-auto py-4 px-2 sm:px-4 pb-16 text-slate-300">
      {/* Cabeçalho direto */}
      <div className="mb-6">
        <h1 className="text-2xl sm:text-3xl font-bold text-white tracking-tight">
          Termos e Condições
        </h1>
        <p className="text-xs sm:text-sm text-slate-400 mt-1">
          Regulamento de uso da plataforma, compras e política de privacidade.
        </p>
      </div>

      {/* Navegação direta por abas */}
      <div className="flex border-b border-slate-800 gap-2 sm:gap-6 overflow-x-auto text-xs sm:text-sm mb-6">
        {tabs.map((tab) => {
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setTab(tab.id)}
              className={`pb-3 font-semibold transition-colors whitespace-nowrap border-b-2 cursor-pointer ${
                isActive
                  ? 'text-purple-400 border-purple-500'
                  : 'text-slate-400 border-transparent hover:text-slate-200'
              }`}
            >
              {tab.title}
            </button>
          );
        })}
      </div>

      {/* Conteúdo direto */}
      <div className="space-y-6 text-sm leading-relaxed text-slate-300">
        
        {/* ========================================================= */}
        {/* 1. TERMOS DE USO DO CARDUMY */}
        {/* ========================================================= */}
        {activeTab === 'uso' && (
          <article className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-white">Termos de Uso do Cardumy</h2>
              <p className="text-xs text-slate-500 mt-0.5">Versão atualizada em 17/10/2026</p>
            </div>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">1. Quem somos</h3>
              <p>
                <strong>1.1.</strong> O Cardumy (<a href="https://www.cardumy.com" target="_blank" rel="noreferrer" className="text-purple-400 hover:underline">www.cardumy.com</a>), cujo e-mail de contato é: <a href="mailto:contato@cardumy.com" className="text-purple-400 hover:underline">contato@cardumy.com</a>.
              </p>
              <p>
                <strong>1.2.</strong> Estes Termos se aplicam a todos os usuários e se complementam com os Termos de Compras, Pagamentos e Ingressos, a Política de Privacidade e Cookies e, para lojas, os Termos do Lojista. Havendo conflito, prevalece o documento específico do assunto.
              </p>
              <p>
                <strong>1.3.</strong> Ao criar uma conta, você declara ter lido e aceito estes documentos.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">2. O que o Cardumy é — e o que não é</h3>
              <p>
                <strong>2.1.</strong> O Cardumy é uma plataforma de software para a comunidade de Trading Card Games, com gestão de coleções, wishlist e offerlist, deckbuilder, consulta de cartas, CardLink, avaliações, torneios, rankings e ferramentas para que lojas cadastradas (“Lojistas”) vendam produtos e ingressos.
              </p>
              <p>
                <strong>2.2. Vendas e Pagamentos:</strong> Nas compras em lojas, o vendedor é sempre o Lojista. O pagamento vai diretamente para a conta do Lojista. O Cardumy não recebe, guarda nem repassa valores, não é instituição de pagamento e não é o vendedor. O Cardumy fornece a tecnologia, identifica o Lojista e mantém um canal de suporte.
              </p>
              <p>
                <strong>2.3. CardLink:</strong> O Cardumy apenas aproxima usuários com cartas compatíveis. As negociações acontecem diretamente entre os usuários, fora da plataforma. O Cardumy não participa da negociação, não recebe valores e não garante entrega, estado ou autenticidade das cartas.
              </p>
              <p>
                <strong>2.4.</strong> Nada nestes Termos afasta direitos garantidos por lei ao consumidor.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">3. Cadastro e conta</h3>
              <p>
                <strong>3.1.</strong> Você deve informar dados verdadeiros e mantê-los atualizados. A conta é pessoal e intransferível, e cada pessoa pode ter apenas uma conta.
              </p>
              <p>
                <strong>3.2.</strong> Você é responsável pela guarda da sua senha e deve avisar o Cardumy imediatamente em caso de uso não autorizado.
              </p>
              <p>
                <strong>3.3. Idade mínima:</strong> O Cardumy é destinado a pessoas com 12 anos ou mais. Contas de menores de 12 anos não são permitidas e serão excluídas quando identificadas.
              </p>
              <p>
                <strong>3.4.</strong> Adolescentes de 12 a 17 anos só podem usar o Cardumy com autorização e supervisão dos pais ou responsáveis legais, que respondem pelos atos do adolescente na plataforma. Compras de produtos ou ingressos devem ser feitas ou autorizadas pelo responsável.
              </p>
              <p>
                <strong>3.5.</strong> É proibido usar nome, apelido ou imagem que imite outra pessoa, loja, marca ou o próprio Cardumy. Termos como “loja”, “store” ou “oficial” são reservados a Lojistas verificados.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">4. CardLink e compartilhamento de contato</h3>
              <p>
                <strong>4.1.</strong> Ao incluir cartas na sua wishlist ou offerlist, você permite que elas sejam usadas para encontrar correspondências com outros usuários.
              </p>
              <p>
                <strong>4.2.</strong> Seu contato (por exemplo, WhatsApp) só é revelado a outro usuário depois da sua autorização expressa para aquela solicitação. Você pode recusar qualquer solicitação.
              </p>
              <p>
                <strong>4.3.</strong> Quem recebe um contato só pode usá-lo para a negociação em questão. É proibido repassar o contato, enviar propaganda ou importunar o outro usuário.
              </p>
              <p>
                <strong>4.4. Segurança:</strong> Confira a reputação da outra parte, prefira encontros em locais públicos ou lojas e desconfie de pagamentos antecipados a desconhecidos. O Cardumy nunca pede sua senha nem pagamentos por WhatsApp.
              </p>
              <p>
                <strong>4.5.</strong> As negociações feitas via CardLink são de responsabilidade exclusiva das partes envolvidas.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">5. Conteúdo dos usuários e avaliações</h3>
              <p>
                <strong>5.1.</strong> Você é responsável pelo conteúdo que publica (perfil, coleções, decks, decklists e avaliações) e concede ao Cardumy uma licença gratuita e não exclusiva para exibi-lo na plataforma enquanto estiver publicado, inclusive no histórico de torneios e rankings.
              </p>
              <p>
                <strong>5.2.</strong> Avaliações devem refletir experiências reais. São proibidas avaliações falsas, combinadas, retaliatórias ou feitas com contas múltiplas. O Cardumy pode remover avaliações que violem estas regras.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">6. Propriedade intelectual</h3>
              <p>
                <strong>6.1.</strong> Nomes, imagens, textos de cartas, logotipos e marcas dos jogos pertencem às respectivas empresas titulares. O Cardumy os exibe apenas para referência e consulta, sem afiliação ou endosso.
              </p>
              <p>
                <strong>6.2.</strong> O software, o design, a marca Cardumy, o sistema de pontuação e as bases de rankings e estatísticas pertencem ao Cardumy.
              </p>
              <p>
                <strong>6.3.</strong> Titulares de direitos podem solicitar a remoção de conteúdo pelo e-mail <a href="mailto:contato@cardumy.com" className="text-purple-400 hover:underline">contato@cardumy.com</a>.
              </p>
              <p>
                <strong>6.4.</strong> É estritamente proibido anunciar ou negociar cartas falsificadas, réplicas apresentadas como originais ou qualquer material ilícito.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">7. Condutas proibidas</h3>
              <p>É proibido, entre outras condutas:</p>
              <ul className="list-disc pl-5 space-y-1 text-slate-300">
                <li>Aplicar golpes, fraudes ou simular negociações;</li>
                <li>Criar perfis falsos ou se passar por outra pessoa, loja ou marca;</li>
                <li>Praticar discurso de ódio, discriminação, ameaça, assédio ou qualquer ato ilícito;</li>
                <li>Manipular avaliações, rankings, resultados de torneios ou preços;</li>
                <li>Coletar dados de outros usuários sem autorização;</li>
                <li>Explorar falhas técnicas, acessar áreas restritas ou automatizar o uso sem autorização;</li>
                <li>Criar nova conta para contornar suspensão ou banimento.</li>
              </ul>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">8. Denúncias, moderação e penalidades</h3>
              <p>
                <strong>8.1.</strong> Denúncias podem ser feitas por ticket de suporte ou pelo e-mail <a href="mailto:contato@cardumy.com" className="text-purple-400 hover:underline">contato@cardumy.com</a>.
              </p>
              <p>
                <strong>8.2.</strong> Conforme a gravidade, o Cardumy poderá aplicar: advertência, remoção de conteúdo, restrição de funcionalidades, suspensão temporária ou encerramento da conta.
              </p>
              <p>
                <strong>8.3.</strong> Fraude comprovada, perfil falso, discurso de ódio e condutas criminosas resultam em banimento imediato.
              </p>
              <p>
                <strong>8.4.</strong> O usuário pode recorrer em até 15 dias, por ticket de suporte, recebendo resposta fundamentada.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">9. Responsabilidade</h3>
              <p>
                <strong>9.1.</strong> O Cardumy responde pelos serviços que presta, nos termos da legislação brasileira.
              </p>
              <p>
                <strong>9.2.</strong> O Cardumy não responde por negociações feitas fora da plataforma, atos de outros usuários, produtos ou entregas de Lojistas, ou indisponibilidades temporárias fora de seu controle.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">10. Encerramento da conta</h3>
              <p>
                Você pode solicitar o encerramento da sua conta a qualquer momento pelo e-mail <a href="mailto:contato@cardumy.com" className="text-purple-400 hover:underline">contato@cardumy.com</a> ou através do menu de configurações.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">11. Alterações destes Termos</h3>
              <p>
                Alterações nestes Termos serão comunicadas previamente na plataforma. O uso contínuo da plataforma após a data de vigência constitui aceite das alterações.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">12. Lei aplicável e foro</h3>
              <p>
                Aplica-se a lei brasileira. Para dirimir quaisquer litígios, fica eleito o foro competente nos termos da legislação aplicável.
              </p>
            </section>
          </article>
        )}

        {/* ========================================================= */}
        {/* 2. TERMOS DE COMPRAS, PAGAMENTOS E INGRESSOS */}
        {/* ========================================================= */}
        {activeTab === 'compras' && (
          <article className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-white">Termos de Compras, Pagamentos e Ingressos</h2>
              <p className="text-xs text-slate-500 mt-0.5">Versão atualizada em 17/10/2026</p>
            </div>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">1. Aplicação</h3>
              <p>
                Estes Termos se aplicam a todas as compras de produtos físicos, virtuais e ingressos de eventos e torneios oferecidos por Lojistas através do Cardumy.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">2. Quem vende</h3>
              <p>
                <strong>2.1.</strong> O vendedor é sempre o Lojista parceiro identificado na página do produto e no resumo do pedido. O contrato de compra e venda é firmado diretamente entre o comprador e o Lojista.
              </p>
              <p>
                <strong>2.2.</strong> O Lojista é integralmente responsável pelo estoque, precificação, emissão de documento fiscal, embalagem, envio, atendimento e cumprimento de garantias.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">3. Oferta e preço</h3>
              <p>
                <strong>3.1.</strong> Os preços, taxas de frete e prazos estimados são exibidos com clareza antes da conclusão do pagamento.
              </p>
              <p>
                <strong>3.2.</strong> Em casos excepcionais de erro crasso e evidente de precificação decorrente de falha sistêmica, o pedido poderá ser cancelado com devolução imediata do valor pago.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">4. Pagamentos</h3>
              <p>
                <strong>4.1. Pix:</strong> O pagamento é direcionado diretamente para a conta do Lojista. A confirmação ocorre de forma automática ou mediante conferência de comprovante conforme as instruções da loja.
              </p>
              <p>
                <strong>4.2. Cartão de Crédito:</strong> Quando disponível, o processamento ocorre via parceiros de pagamento integrados pelo Lojista (como Mercado Pago). O Cardumy não armazena dados de cartão.
              </p>
              <p>
                <strong>4.3.</strong> Efetue pagamentos exclusivamente através dos canais oficiais integrados no checkout da plataforma.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">5. Entrega e retirada</h3>
              <p>
                O Lojista é responsável por disponibilizar o código de rastreamento ou indicar as instruções e horários para retirada presencial no balcão de sua loja.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">6. Direito de arrependimento</h3>
              <p>
                Conforme o Art. 49 do Código de Defesa do Consumidor, em compras realizadas à distância você pode solicitar o cancelamento e a devolução em até <strong>7 (sete) dias corridos</strong> após o recebimento do produto, com reembolso integral dos valores pagos, inclusive o frete de envio.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">7. Produtos com avaria ou divergência</h3>
              <p>
                Caso o item entregue apresente divergência de idioma, estado de conservação (condition) diferente do anunciado ou defeito de fabricação, o comprador poderá solicitar substituição, abatimento ou cancelamento com devolução nos prazos previstos em lei.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">8. Cancelamento pelo Lojista e Reembolsos</h3>
              <p>
                <strong>8.1.</strong> Caso o Lojista não possua estoque ou cancele o pedido, deverá efetuar a restituição total dos valores pagos pelo mesmo meio de pagamento em até 7 dias úteis.
              </p>
              <p>
                <strong>8.2.</strong> Em caso de dificuldades de contato com o Lojista, o usuário pode acionar a Central de Suporte do Cardumy para intermediação.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">9. Créditos de loja</h3>
              <p>
                Créditos concedidos por lojistas são vinculados à respectiva loja emitente e não representam moeda corrente, não podendo ser sacados em espécie nem transferidos para outras lojas.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">10. Ingressos para eventos e torneios</h3>
              <p>
                <strong>10.1.</strong> O ingresso de torneio é pessoal e confirma a inscrição do jogador no evento.
              </p>
              <p>
                <strong>10.2. Cancelamento por iniciativa do jogador:</strong> Pode ser solicitado em até <strong>48 horas</strong> antes do início previsto da rodada inicial, com reembolso integral ou crédito conforme a política do organizador.
              </p>
              <p>
                <strong>10.3. Adiamento ou cancelamento do evento:</strong> Em caso de alteração de data, horário ou cancelamento pelo organizador, o participante terá direito ao reembolso integral ou manutenção da inscrição para a nova data.
              </p>
              <p>
                <strong>10.4.</strong> Desclassificações por conduta antidesportiva, infração grave de regras oficiais do TCG ou fraude não ensejam direito a reembolso.
              </p>
            </section>
          </article>
        )}

        {/* ========================================================= */}
        {/* 3. POLÍTICA DE PRIVACIDADE E COOKIES */}
        {/* ========================================================= */}
        {activeTab === 'privacidade' && (
          <article className="space-y-6">
            <div>
              <h2 className="text-xl font-bold text-white">Política de Privacidade e Cookies</h2>
              <p className="text-xs text-slate-500 mt-0.5">Versão atualizada em 17/10/2026</p>
            </div>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">1. Controlador e contato</h3>
              <p>
                O tratamento de dados é realizado pelo Cardumy em conformidade com a Lei Geral de Proteção de Dados (LGPD — Lei nº 13.709/2018). Para exercer seus direitos de titular ou esclarecer dúvidas, entre em contato através de: <a href="mailto:contato@cardumy.com?subject=Privacidade" className="text-purple-400 hover:underline">contato@cardumy.com</a>.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">2. Dados coletados e finalidades</h3>
              <ul className="list-disc pl-5 space-y-2 text-slate-300">
                <li>
                  <strong className="text-white">Dados de Cadastro:</strong> Nome, apelido de usuário (nickname), endereço de e-mail e senha criptografada. Finalidade: identificação, autenticação e comunicação sobre sua conta.
                </li>
                <li>
                  <strong className="text-white">Coleções, Wishlists e Decks:</strong> Informações de cartas registradas e decks criados. Finalidade: funcionamento do ecossistema, matching de trocas e rankings comunitários.
                </li>
                <li>
                  <strong className="text-white">Dados de Compras e Endereço:</strong> Itens adquiridos, endereço de entrega e CPF (quando exigido para emissão de nota fiscal pelo lojista). Finalidade: viabilizar a entrega e faturamento do pedido pelo Lojista.
                </li>
                <li>
                  <strong className="text-white">Telefone/WhatsApp (opcional):</strong> Contato informado para interações no CardLink, exibido somente após sua autorização explícita.
                </li>
              </ul>
              <p className="text-xs text-slate-400">
                O Cardumy não comercializa dados pessoais nem realiza publicidade direcionada a terceiros.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">3. Compartilhamento de dados</h3>
              <p>
                Seus dados não são vendidos. O compartilhamento ocorre exclusivamente com:
              </p>
              <ul className="list-disc pl-5 space-y-1 text-slate-300">
                <li>Lojistas parceiros, estritamente na medida necessária para faturamento e despacho de pedidos realizados;</li>
                <li>Provedores de infraestrutura técnica (hospedagem em nuvem, banco de dados e envio de e-mails transacionais), sob estritos deveres de sigilo e segurança;</li>
                <li>Autoridades judiciais ou regulatórias, apenas quando requerido por ordem formal e legal.</li>
              </ul>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">4. Cookies</h3>
              <p>
                Utilizamos exclusivamente <strong>cookies estritamente necessários</strong> para garantir a segurança da sessão, manter o usuário logado e memorizar o jogo de cartas selecionado. Não utilizamos cookies invasivos de rastreamento publicitário.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">5. Seus direitos como titular (LGPD)</h3>
              <p>
                Você tem direito a confirmar a existência de tratamento, acessar seus dados, corrigir dados incompletos ou inexatos, solicitar a eliminação dos dados ou revogar o consentimento. Basta solicitar pelo e-mail <a href="mailto:contato@cardumy.com?subject=Privacidade" className="text-purple-400 hover:underline">contato@cardumy.com</a>.
              </p>
            </section>

            <section className="space-y-2">
              <h3 className="text-base font-semibold text-white">6. Segurança da informação</h3>
              <p>
                Adotamos medidas técnicas adequadas, como conexões protegidas por criptografia HTTPS/SSL, senhas com hashing seguro e controle rígido de privilégios de acesso aos bancos de dados.
              </p>
            </section>
          </article>
        )}

      </div>

      {/* Nota de rodapé simples e direta */}
      <div className="mt-12 pt-6 border-t border-slate-800 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 text-xs text-slate-500">
        <div>
          Dúvidas sobre estes termos ou solicitações de privacidade? Envie um e-mail para{' '}
          <a href="mailto:contato@cardumy.com" className="text-purple-400 hover:underline">
            contato@cardumy.com
          </a>.
        </div>
        <Link to="/termos-do-lojista" className="text-purple-400 hover:underline">
          É lojista parceiro? Consulte os Termos do Lojista
        </Link>
      </div>
    </div>
  );
};
